import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, Input, notification, Table, Tabs, Tag, Tooltip } from 'antd';
import dayjs from 'dayjs';

import { BiExport } from 'react-icons/bi';
import { FaSearch } from 'react-icons/fa';
import { FiFilter } from 'react-icons/fi';
import { HiOutlineRefresh } from 'react-icons/hi';

import '../../../css/UserAdmin/Global.css';

import Footer from '../Footer';
import Header from '../NewHeader';
import SideNav from '../SideNav';

const { TabPane } = Tabs;

const PAYMENT_CATEGORIES = {
    ALL: 'All Payments',
    TUITION: 'Tuition Payments',
    RESERVATION: 'Reservation Fees',
    EXAM: 'Exam Fees',
};

const PaymentHistory = () => {
    const navigate = useNavigate();
    const [isSideNavOpen, setIsSideNavOpen] = useState(false);
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);
    const [rawPaymentsData, setRawPaymentsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [tableFilters, setTableFilters] = useState({});
    const [sorter, setSorter] = useState({});
    const [selectedPaymentView, setSelectedPaymentView] = useState(PAYMENT_CATEGORIES.ALL);

    const toggleSideNav = useCallback(() => setIsSideNavOpen(prev => !prev), []);

    useEffect(() => {
        const handleResize = () => setIsSmallScreen(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // This function processes data from the backend into a more unified structure
    const processFetchedData = (tuitionData = [], otherData = []) => {
        const processedTuition = tuitionData.map(p => ({
            ...p, // Includes studentID, fullName, paymentAmount, paymentMethod, status (tuition), verifiedBy, referenceNumber, historyStatus, historyDescription, paymentId, checkoutId
            key: `tuition-${p._id || p.referenceNumber || Math.random()}`, // Prefer _id from TuitionPayment
            paymentCategory: PAYMENT_CATEGORIES.TUITION,
            paymentDate: p.createdAt || p.timestamp, // TuitionPayment has createdAt and timestamp
            displayName: p.fullName,
            displayID: p.studentID,
        }));

        const processedOthers = otherData.map(p => ({
            ...p,
            key: `other-${p._id || p.referenceNumber || Math.random()}`, // Prefer _id from PaymentHistory
            paymentCategory: p.description === "Exam Fee Payment" ? PAYMENT_CATEGORIES.EXAM :
                p.description === "Reservation Fee Payment" ? PAYMENT_CATEGORIES.RESERVATION :
                    'Other Fee', // Fallback category
            paymentDate: p.createdAt,
            displayName: p.applicantName,
            displayID: p.applicantID,
            paymentAmount: p.amount,
            historyStatus: p.status,
            historyDescription: p.description,
        }));

        const combined = [...processedTuition, ...processedOthers];
        return combined.sort((a, b) => dayjs(b.paymentDate).unix() - dayjs(a.paymentDate).unix());
    };


    const fetchAllPaymentData = async () => {
        setLoading(true);
        try {
            const [tuitionRes, otherRes] = await Promise.all([
                fetch(`${process.env.REACT_APP_API_URL}/api/admin/paymentHistory/combined-payments`),
                fetch(`${process.env.REACT_APP_API_URL}/api/admin/paymentHistory/other-payments`) // Ensure this endpoint exists
            ]);

            let tuitionData = [];
            let otherData = [];

            if (tuitionRes.ok) {
                tuitionData = await tuitionRes.json();
                if (!Array.isArray(tuitionData)) {
                    console.error('Tuition data is not an array:', tuitionData);
                    tuitionData = []; // Default to empty array on error
                }
            } else {
                console.error('Failed to fetch tuition payments:', tuitionRes.statusText);
                notification.error({ message: 'Fetch Error', description: 'Could not fetch tuition payment data.' });
            }

            if (otherRes.ok) {
                otherData = await otherRes.json();
                if (!Array.isArray(otherData)) {
                    console.error('Other payments data is not an array:', otherData);
                    otherData = []; // Default to empty array on error
                }
            } else {
                console.error('Failed to fetch other payments:', otherRes.statusText);
                notification.error({ message: 'Fetch Error', description: 'Could not fetch other payment data (exam/reservation).' });
            }

            setRawPaymentsData(processFetchedData(tuitionData, otherData));

        } catch (error) {
            console.error("Error fetching all payment data:", error);
            notification.error({ message: 'Fetch Error', description: 'Could not fetch payment history. Check network or try again.' });
            setRawPaymentsData([]);
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        fetchAllPaymentData();
    }, []);

    const handleSearch = (value) => {
        setSearchTerm(value);
    };

    const handleClearFilters = () => {
        setTableFilters({});
        setSorter({});
        setSearchTerm('');
    };

    const handleRefresh = () => {
        fetchAllPaymentData();
    };

    const createLog = async (action, detail) => {
        try {
            const userID = localStorage.getItem('userID');
            const accountName = localStorage.getItem('fullName');
            const role = localStorage.getItem('role');

            if (!userID || !accountName || !role) {
                console.warn('User info not found in localStorage for logging.');
                return;
            }

            const logData = { userID, accountName, role, action, detail };
            await fetch(`${process.env.REACT_APP_API_URL}/api/admin/system-logs`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(logData),
            });
        } catch (error) {
            console.error('Failed to record system log:', error);
        }
    };

    const handleExport = () => {
        const dataToExport = filteredDataSource; // Export currently filtered view
        if (dataToExport.length === 0) {
            notification.warn({ message: 'No Data', description: 'There is no data in the current view to export.' });
            return;
        }

        const headers = [
            "Payment Date", "Payment Category", "ID (Student/Applicant)", "Name", "Amount", "Payment Method",
            "Reference No.", "System Status", "Verified By", "Gateway Status", "Gateway Description",
            "Gateway Payment ID", "Gateway Checkout ID"
        ];

        const csvRows = [
            headers.join(','),
            ...dataToExport.map(row => [
                `"${row.paymentDate ? dayjs(row.paymentDate).format('YYYY-MM-DD HH:mm:ss') : 'N/A'}"`,
                `"${row.paymentCategory || 'N/A'}"`,
                `"${row.displayID || ''}"`,
                `"${row.displayName || ''}"`,
                row.paymentAmount || 0,
                `"${row.paymentMethod || ''}"`,
                `"${row.referenceNumber || ''}"`,
                `"${row.paymentCategory === PAYMENT_CATEGORIES.TUITION ? (row.status || 'N/A') : 'N/A'}"`, // System Status (TuitionPayment.status)
                `"${row.paymentCategory === PAYMENT_CATEGORIES.TUITION ? (row.verifiedBy || 'N/A') : 'N/A'}"`,
                `"${row.historyStatus || 'N/A'}"`, // Gateway Status
                `"${(row.historyDescription || '').replace(/"/g, '""')}"`,
                `"${row.paymentId || ''}"`,
                `"${row.checkoutId || ''}"`
            ].join(','))
        ];
        const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        const currentDate = dayjs().format('YYYY-MM-DD');
        const viewTypeForFile = selectedPaymentView.toLowerCase().replace(/\s+/g, '-');
        const fileName = `payment-history-${viewTypeForFile}-${currentDate}.csv`;
        link.setAttribute('download', fileName);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        notification.success({ message: 'Export Successful', description: `${fileName} has been downloaded.` });
        createLog('Export', `Exported ${selectedPaymentView} report: ${fileName}`);
    };

    const filteredDataSource = useMemo(() => {
        let dataToFilter = rawPaymentsData;

        if (selectedPaymentView !== PAYMENT_CATEGORIES.ALL) {
            dataToFilter = rawPaymentsData.filter(item => item.paymentCategory === selectedPaymentView);
        }

        const lowerSearchTerm = searchTerm.toLowerCase();

        return dataToFilter.filter(item => {
            const matchesSearch = searchTerm ? (
                (item.displayID && item.displayID.toLowerCase().includes(lowerSearchTerm)) ||
                (item.displayName && item.displayName.toLowerCase().includes(lowerSearchTerm)) ||
                (item.referenceNumber && item.referenceNumber.toLowerCase().includes(lowerSearchTerm)) ||
                (item.paymentMethod && item.paymentMethod.toLowerCase().includes(lowerSearchTerm)) ||
                (item.paymentCategory && item.paymentCategory.toLowerCase().includes(lowerSearchTerm)) ||
                (item.status && item.status.toLowerCase().includes(lowerSearchTerm)) || // System status for tuition, gateway for others (before mapping to historyStatus)
                (item.historyStatus && item.historyStatus.toLowerCase().includes(lowerSearchTerm)) || // Gateway status
                (item.verifiedBy && item.paymentCategory === PAYMENT_CATEGORIES.TUITION && item.verifiedBy.toLowerCase().includes(lowerSearchTerm))
            ) : true;

            const matchesFilters = Object.keys(tableFilters).every(key => {
                const filterValue = tableFilters[key];
                if (!filterValue || filterValue.length === 0) return true;

                // Handle specific filter keys
                if (key === 'status' && item.paymentCategory === PAYMENT_CATEGORIES.TUITION) { // System Status
                    return filterValue.includes(String(item.status));
                }
                if (key === 'historyStatus') { // Gateway Status
                    return filterValue.includes(String(item.historyStatus));
                }
                if (key === 'paymentCategory') {
                    return filterValue.includes(String(item.paymentCategory));
                }
                return filterValue.includes(String(item[key]));
            });
            return matchesSearch && matchesFilters;
        });
    }, [rawPaymentsData, selectedPaymentView, searchTerm, tableFilters]);

    const columns = [
        {
            title: 'Payment Date',
            dataIndex: 'paymentDate',
            key: 'paymentDate',
            width: 170,
            render: (text) => text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : 'N/A',
            sorter: (a, b) => dayjs(a.paymentDate).unix() - dayjs(b.paymentDate).unix(),
            sortOrder: sorter.columnKey === 'paymentDate' ? sorter.order : null,
            fixed: 'left',
        },
        {
            title: 'Category',
            dataIndex: 'paymentCategory',
            key: 'paymentCategory',
            width: 180,
            filters: Object.values(PAYMENT_CATEGORIES).map(val => ({ text: val, value: val })),
            onFilter: (value, record) => record.paymentCategory === value,
            filteredValue: tableFilters.paymentCategory || null,
            sorter: (a, b) => (a.paymentCategory || '').localeCompare(b.paymentCategory || ''),
            sortOrder: sorter.columnKey === 'paymentCategory' ? sorter.order : null,
        },
        {
            title: 'ID (Student/App.)',
            dataIndex: 'displayID',
            key: 'displayID',
            width: 150,
        },
        {
            title: 'Name',
            dataIndex: 'displayName',
            key: 'displayName',
            width: 200,
        },
        {
            title: 'Amount',
            dataIndex: 'paymentAmount',
            key: 'paymentAmount',
            width: 120,
            align: 'right',
            render: (amount) => amount ? `₱${Number(amount).toFixed(2)}` : 'N/A',
        },
        {
            title: 'Payment Method',
            dataIndex: 'paymentMethod',
            key: 'paymentMethod',
            width: 150,
            filters: [
                { text: 'GCash (Online)', value: 'gcash' },
                { text: 'Maya (Online)', value: 'paymaya' },
                { text: 'Payment Link', value: 'link' },
                { text: 'Cash', value: 'cash' },
                { text: 'Bank Transfer', value: 'bank' },
            ],
            onFilter: (value, record) => record.paymentMethod === value,
            filteredValue: tableFilters.paymentMethod || null,
        },
        {
            title: 'Reference No.',
            dataIndex: 'referenceNumber',
            key: 'referenceNumber',
            width: 200,
        },
        {
            title: 'System Status (Tuition)',
            dataIndex: 'status', // This is TuitionPayment.status
            key: 'status',
            width: 180,
            render: (text, record) => {
                if (record.paymentCategory !== PAYMENT_CATEGORIES.TUITION) return <Tag>N/A</Tag>;
                let color = 'default';
                // Adjust based on your TuitionPayment.status values
                if (text === 'Verified' || text === 'Manually Approved' || text === 'successful') color = 'green';
                else if (text === 'Pending' || text === 'pending') color = 'orange';
                else if (text === 'Failed' || text === 'Rejected' || text === 'failed' || text === 'cancelled') color = 'red';
                return <Tag color={color}>{text || 'N/A'}</Tag>;
            },
            filters: [ // Populate with actual TuitionPayment.status values
                { text: 'Verified', value: 'Verified' },
                { text: 'Pending', value: 'Pending' },
                { text: 'Rejected', value: 'Rejected' },
                { text: 'Successful (Gateway)', value: 'successful' }, // If gateway status is also in TuitionPayment.status
            ],
            // This onFilter assumes item.status is the one to filter for tuition
            onFilter: (value, record) => record.paymentCategory === PAYMENT_CATEGORIES.TUITION && record.status === value,
            filteredValue: tableFilters.status || null,
        },
        {
            title: 'Verified By (Tuition)',
            dataIndex: 'verifiedBy',
            key: 'verifiedBy',
            width: 180,
            render: (text, record) => record.paymentCategory === PAYMENT_CATEGORIES.TUITION ? (text || 'N/A') : <Tag>N/A</Tag>,
        },
        {
            title: 'Gateway Status',
            dataIndex: 'historyStatus', // Standardized field for gateway status
            key: 'historyStatus',
            width: 150,
            render: (status) => {
                let color = 'default';
                if (status === 'succeeded' || status === 'paid' || status === 'successful') color = 'green'; // common successful statuses
                else if (status === 'pending' || status === 'processing') color = 'orange';
                else if (status === 'failed' || status === 'expired' || status === 'cancelled') color = 'red';
                return status ? <Tag color={color}>{status}</Tag> : <Tag>N/A</Tag>;
            },
            filters: [
                { text: 'Succeeded/Paid', value: 'succeeded' }, // Value should match actual data
                { text: 'Successful', value: 'successful' },
                { text: 'Pending', value: 'pending' },
                { text: 'Failed', value: 'failed' },
                { text: 'Expired', value: 'expired' },
                { text: 'Cancelled', value: 'cancelled' },
            ],
            onFilter: (value, record) => record.historyStatus === value,
            filteredValue: tableFilters.historyStatus || null,
        },
        {
            title: 'Gateway Desc./Type',
            dataIndex: 'historyDescription', // Standardized field
            key: 'historyDescription',
            width: 250,
            ellipsis: { showTitle: false },
            render: (description) => <Tooltip placement="topLeft" title={description}>{description || 'N/A'}</Tooltip>,
        },
        {
            title: 'Gateway Checkout ID',
            dataIndex: 'checkoutId',
            key: 'checkoutId',
            width: 220,
            ellipsis: true,
        },
    ];

    return (
        <div className={`admin-page-container ${isSideNavOpen ? 'admin-sidenav-open' : ''}`}>
            <Header onMenuClick={toggleSideNav} isSideNavOpen={isSideNavOpen} />
            <div className="admin-content-wrapper">
                <div className="admin-sidenav-container">
                    <SideNav isOpen={isSideNavOpen} onClose={toggleSideNav} isSmallScreen={isSmallScreen} />
                </div>
                <main className="admin-main-content-area">
                    <div className="page-title">
                        <p className="heading">Payment History</p>
                    </div>

                    <Tabs activeKey={selectedPaymentView} onChange={setSelectedPaymentView} style={{ marginBottom: 16 }}>
                        {Object.entries(PAYMENT_CATEGORIES).map(([key, value]) => (
                            <TabPane tab={value} key={value} />
                        ))}
                    </Tabs>

                    <div className="table-functions">
                        <div className="left-tools">
                            <Button icon={<FiFilter />} onClick={handleClearFilters}>Clear Filters</Button>
                            <Button icon={<HiOutlineRefresh />} onClick={handleRefresh} loading={loading}>Refresh</Button>
                            <Button icon={<BiExport />} onClick={handleExport} disabled={filteredDataSource.length === 0}>Export CSV</Button>
                        </div>
                        <div className="right-tools">
                            <Input
                                placeholder="Search ID, Name, Ref No..."
                                allowClear
                                style={{ width: 300 }}
                                value={searchTerm}
                                onChange={(e) => handleSearch(e.target.value)}
                                suffix={<FaSearch style={{ color: '#aaa' }} />}
                            />
                        </div>
                    </div>

                    <Table
                        style={{ width: '100%', flex: 1, marginTop: '16px' }}
                        columns={columns}
                        dataSource={filteredDataSource}
                        loading={loading}
                        bordered
                        scroll={{ x: 2800 }} // Adjust based on final column widths
                        pagination={{
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
                            pageSizeOptions: ['10', '20', '50', '100'],
                        }}
                        onChange={(pagination, filters, newSorter) => {
                            setTableFilters(filters);
                            setSorter(newSorter);
                        }}
                    />
                </main>
            </div>
            <Footer />
        </div>
    );
};

export default PaymentHistory;